import * as Cesium from "cesium";

export function getEntitiesWithInvalidLineDistances(viewer, currentDataSource) {
  if (!currentDataSource || !currentDataSource.entities) return [];

  const MAX_DISTANCE_METERS = 4.0;
  const ellipsoid = (viewer && viewer.scene && viewer.scene.globe && viewer.scene.globe.ellipsoid) || Cesium.Ellipsoid.WGS84;

  // Horizontal distance (ignore height) in meters
  function horizontalDistance(cart1, cart2) {
    if (!cart1 || !cart2) return Number.POSITIVE_INFINITY;

    const c1 = ellipsoid.cartesianToCartographic(cart1);
    const c2 = ellipsoid.cartesianToCartographic(cart2);
    if (!c1 || !c2) return Number.POSITIVE_INFINITY;

    c1.height = 0;
    c2.height = 0;

    const geodesic = new Cesium.EllipsoidGeodesic(c1, c2, ellipsoid);
    return geodesic.surfaceDistance;
  }

  function normalizeForCompare(str) {
    if (str === null || str === undefined) return "";
    return String(str).toLowerCase().replace(/\s+/g, "").replace(/[-_]+/g, "");
  }

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      if (typeof property.getValue === "function") return property.getValue();
      return property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      if (typeof property.getValue === "function") return property.getValue();
      return property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  // properties.id → fallback entity.id
  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  function buildIdToInfoMap(viewer, currentDataSource) {
    const map = new Map();
    if (!viewer || !viewer.dataSources) return map;

    const collection = viewer.dataSources;

    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || ds === currentDataSource || !ds.entities) continue;

      const layerName = ds.layerName;
      if (!layerName) continue;

      const layerNorm = normalizeForCompare(layerName);
      const entities = ds.entities.values || ds.entities;
      if (!Array.isArray(entities)) continue;

      for (let j = 0; j < entities.length; j++) {
        const entity = entities[j];
        if (!entity || !entity.properties) continue;

        const idValue = getPropertyValueFromProps(entity.properties, "id");
        if (idValue === null || idValue === undefined) continue;

        const key = String(idValue);
        if (!map.has(key)) {
          map.set(key, { layerNorm, entity });
        }
      }
    }

    return map;
  }

  function getFirstAndLastPositions(entity, time) {
    if (!entity || !entity.polyline) return null;

    const polyline = entity.polyline;
    let positionsProp = polyline.positions || polyline._positions;
    if (!positionsProp) return null;

    let positions = null;

    if (typeof positionsProp.getValue === "function") {
      positions = positionsProp.getValue(time);
    } else if (Array.isArray(positionsProp)) {
      positions = positionsProp;
    } else if (Array.isArray(positionsProp._value)) {
      positions = positionsProp._value;
    }

    if (!Array.isArray(positions) || positions.length === 0) return null;

    return { first: positions[0], last: positions[positions.length - 1] };
  }

  const entities = currentDataSource.entities.values || currentDataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const idToInfoMap = buildIdToInfoMap(viewer, currentDataSource);

  const result = [];
  const seenIds = new Set();

  const time = (viewer && viewer.clock && viewer.clock.currentTime) || Cesium.JulianDate.now();

  // Only return refEntity when id exists in other layers AND type matches that layer
  function checkTypeAndGetRef(sideLabel, sideType, sideId) {
    if (!sideType || !sideId) {
      return { exists: false, typeValid: true, refEntity: null, typeReason: null };
    }

    const info = idToInfoMap.get(String(sideId));
    if (!info) {
      // id not found => ignore this side (no report)
      return { exists: false, typeValid: true, refEntity: null, typeReason: null };
    }

    const normType = normalizeForCompare(sideType);
    const typeValid = info.layerNorm.includes(normType);

    if (!typeValid) {
      // type mismatch => ignore this side entirely (no report)
      return { exists: false, typeValid: true, refEntity: null, typeReason: null };
    }

    return { exists: true, typeValid: true, refEntity: info.entity, typeReason: null };
  }

  function formatMeters(m) {
    if (!Number.isFinite(m)) return "N/A";
    return `${m.toFixed(2)}m`;
  }

  for (let i = 0; i < entities.length; i++) {
    const laneletEntity = entities[i];
    if (!laneletEntity || !laneletEntity.properties) continue;

    const props = laneletEntity.properties;

    const leftType = getPropertyValueFromProps(props, "left_line_type");
    const rightType = getPropertyValueFromProps(props, "right_line_type");
    const leftId = getPropertyValueFromProps(props, "left_line_id");
    const rightId = getPropertyValueFromProps(props, "right_line_id");

    const leftCheck = checkTypeAndGetRef("left", leftType, leftId);
    const rightCheck = checkTypeAndGetRef("right", rightType, rightId);

    const hasAnySide = leftCheck.exists || rightCheck.exists;
    if (!hasAnySide) continue;

    const reasons = [];

    const laneletPos = getFirstAndLastPositions(laneletEntity, time);
    if (!laneletPos || !laneletPos.first) {
      reasons.push("Lanelet polyline positions are missing");
    } else {
      // Right side distance check
      if (rightCheck.exists && rightCheck.refEntity) {
        const rightPos = getFirstAndLastPositions(rightCheck.refEntity, time);
        if (!rightPos || !rightPos.first) {
          reasons.push("Right line polyline positions are missing");
        } else {
          const d = horizontalDistance(laneletPos.first, rightPos.first);
          if (d > MAX_DISTANCE_METERS) {
            reasons.push(`Right line distance mismatch (distance: ${formatMeters(d)}, max: ${MAX_DISTANCE_METERS}m)`);
          }
        }
      }

      // Left side distance check (min(first, last))
      if (leftCheck.exists && leftCheck.refEntity) {
        const leftPos = getFirstAndLastPositions(leftCheck.refEntity, time);
        if (!leftPos || !leftPos.first || !leftPos.last) {
          reasons.push("Left line polyline positions are missing");
        } else {
          const d1 = horizontalDistance(laneletPos.first, leftPos.first);
          const d2 = horizontalDistance(laneletPos.first, leftPos.last);
          const minD = Math.min(d1, d2);
          if (minD > MAX_DISTANCE_METERS) {
            reasons.push(`Left line distance mismatch (distance: ${formatMeters(minD)}, max: ${MAX_DISTANCE_METERS}m)`);
          }
        }
      }
    }

    if (reasons.length > 0) {
      const id = getEntityId(laneletEntity);
      const dedupeKey = id ?? laneletEntity;
      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({ entity: laneletEntity, id, reasons });
      }
    }
  }

  return result;
}
